import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Calendar, Euro, Fuel, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const Analytics = () => {
  const [receipts, setReceipts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [receiptsRes, vehiclesRes] = await Promise.all([
        axios.get('/receipts'),
        axios.get('/vehicles')
      ]);
      setReceipts(receiptsRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Calculate cost trends
  const getCostTrends = () => {
    const now = new Date();
    const days = parseInt(timeRange);
    const trends = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(now.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      const dayReceipts = receipts.filter(r => {
        const receiptDate = new Date(r.created_at).toISOString().split('T')[0];
        return receiptDate === dateStr;
      });

      const totalCost = dayReceipts.reduce((sum, r) => sum + r.amount, 0);
      const fuelCost = dayReceipts.filter(r => r.type === 'fuel').reduce((sum, r) => sum + r.amount, 0);
      const purchaseCost = dayReceipts.filter(r => r.type === 'purchase').reduce((sum, r) => sum + r.amount, 0);

      trends.push({
        date: date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
        total: parseFloat(totalCost.toFixed(2)),
        tanken: parseFloat(fuelCost.toFixed(2)),
        einkauf: parseFloat(purchaseCost.toFixed(2))
      });
    }

    return trends;
  };

  // Vehicle status distribution
  const getVehicleStatusData = () => {
    const available = vehicles.filter(v => v.status === 'available').length;
    const inUse = vehicles.filter(v => v.status === 'in_use').length;
    const maintenance = vehicles.filter(v => v.status === 'maintenance').length;

    return [
      { name: 'Verfügbar', value: available, color: '#10b981' },
      { name: 'Im Einsatz', value: inUse, color: '#3b82f6' },
      { name: 'Wartung', value: maintenance, color: '#f59e0b' }
    ];
  };

  // Cost by vehicle
  const getCostByVehicle = () => {
    const vehicleCosts = {};

    receipts.forEach(receipt => {
      const vehicle = vehicles.find(v => v.vehicle_id === receipt.vehicle_id);
      const key = vehicle ? vehicle.license_plate : 'Unbekannt';

      if (!vehicleCosts[key]) {
        vehicleCosts[key] = 0;
      }
      vehicleCosts[key] += receipt.amount;
    });

    return Object.entries(vehicleCosts)
      .map(([name, value]) => ({
        name,
        kosten: parseFloat(value.toFixed(2))
      }))
      .sort((a, b) => b.kosten - a.kosten)
      .slice(0, 5);
  };

  // Calculate KPIs
  const totalCosts = receipts.reduce((sum, r) => sum + r.amount, 0);
  const fuelCosts = receipts.filter(r => r.type === 'fuel').reduce((sum, r) => sum + r.amount, 0);
  const avgCostPerDay = totalCosts / parseInt(timeRange);
  const totalKm = receipts
    .filter(r => r.km_reading)
    .reduce((sum, r) => sum + (r.km_reading || 0), 0);

  const costTrends = getCostTrends();
  const vehicleStatusData = getVehicleStatusData();
  const costByVehicle = getCostByVehicle();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
            Berichte & Analytics
          </h1>
          <p className="text-zinc-400">Detaillierte Auswertungen und Statistiken</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="7">Letzte 7 Tage</SelectItem>
            <SelectItem value="14">Letzte 14 Tage</SelectItem>
            <SelectItem value="30">Letzte 30 Tage</SelectItem>
            <SelectItem value="90">Letzte 90 Tage</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-950 to-zinc-900 border-blue-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-blue-200">Gesamtkosten</CardTitle>
            <Euro className="h-4 w-4 text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">€{totalCosts.toFixed(2)}</div>
            <div className="text-xs text-blue-300 mt-2">Letzte {timeRange} Tage</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-950 to-zinc-900 border-green-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-green-200">Ø pro Tag</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">€{avgCostPerDay.toFixed(2)}</div>
            <div className="text-xs text-green-300 mt-2">Durchschnitt</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-950 to-zinc-900 border-amber-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-amber-200">Treibstoff</CardTitle>
            <Fuel className="h-4 w-4 text-amber-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">€{fuelCosts.toFixed(2)}</div>
            <div className="text-xs text-amber-300 mt-2">{((fuelCosts / totalCosts) * 100).toFixed(0)}% der Kosten</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-950 to-zinc-900 border-purple-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-purple-200">Belege</CardTitle>
            <Calendar className="h-4 w-4 text-purple-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{receipts.length}</div>
            <div className="text-xs text-purple-300 mt-2">Gesamt erfasst</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Trends */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-white">Kostenentwicklung</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={costTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" stroke="#a1a1aa" />
                <YAxis stroke="#a1a1aa" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Line type="monotone" dataKey="tanken" stroke="#f59e0b" strokeWidth={2} name="Tanken" />
                <Line type="monotone" dataKey="einkauf" stroke="#3b82f6" strokeWidth={2} name="Einkauf" />
                <Line type="monotone" dataKey="total" stroke="#dc2626" strokeWidth={3} name="Gesamt" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Vehicle Status */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-white">Flottenauslastung</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={vehicleStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {vehicleStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Cost by Vehicle */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-white">Kosten pro Fahrzeug (Top 5)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={costByVehicle}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="name" stroke="#a1a1aa" />
              <YAxis stroke="#a1a1aa" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                labelStyle={{ color: '#fff' }}
              />
              <Bar dataKey="kosten" fill="#dc2626" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
